import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import { ModulesProvider } from "./contexts/ModulesContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import UserRecords from "./pages/UserRecords";
import Structures from "./pages/Structures";
import Geography from "./pages/Geography";
import ExportPage from "./pages/ExportPage";
import Settings from "./pages/Settings";
import Personas from "./pages/Personas";
import AdminLogs from "./pages/AdminLogs";
import ScheduledExports from "./pages/ScheduledExports";
import Webhooks from "./pages/Webhooks";
import { Toaster } from "./components/ui/sonner";

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) {
    return <div className="h-screen flex items-center justify-center text-warm-dark/50 text-sm">Chargement…</div>;
  }
  if (!user) {
    const returnTo = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }
  return (
    <ModulesProvider>
      <Layout>{children}</Layout>
    </ModulesProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/personas" element={<ProtectedRoute><Personas /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
              <Route path="/user-records" element={<ProtectedRoute><UserRecords /></ProtectedRoute>} />
              <Route path="/structures" element={<ProtectedRoute><Structures /></ProtectedRoute>} />
              <Route path="/geography" element={<ProtectedRoute><Geography /></ProtectedRoute>} />
              <Route path="/export" element={<ProtectedRoute><ExportPage /></ProtectedRoute>} />
              <Route path="/scheduled-exports" element={<ProtectedRoute><ScheduledExports /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/webhooks" element={<ProtectedRoute><Webhooks /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
